// eslint-disable-next-line import/no-unresolved
import PhotoSwipeLightbox from 'https://www.unpkg.com/photoswipe@5.2.7/dist/photoswipe-lightbox.esm.min.js?module'

const { viewportSize } = window.BAO.Utils

const STATIC_DOTS_MAX = 5
const DOTS_WINDOW_SIZE = 3

export class ProductPhotoSwipe extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['link']
  }

  connectedCallback () {
    super.connectedCallback()

    this.photoSwipeInstance = new PhotoSwipeLightbox({
      loop: true,
      showAnimationDuration: 500,
      initialZoomLevel: 'fit',
      secondaryZoomLevel: this.secondaryZoomLevel,
      maxZoomLevel: 3,
      scaleMode: 'zoom',
      paddingFn: () => this.padding,

      gallery: this.localName,
      children: this.els.link.selector,
      bgOpacity: 1,
      arrowPrev: false,
      arrowNext: false,
      zoom: false,
      close: false,
      counter: false,

      closeOnVerticalDrag: true,

      pswpModule: () => {
        return import(
          // eslint-disable-next-line import/no-unresolved
          'https://www.unpkg.com/photoswipe@5.2.7/dist/photoswipe.esm.min.js?module'
        )
      },
    })

    this.setupPhotoSwipeListeners()
    this.photoSwipeInstance.init()
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(document, 'click', this.handleClick.bind(this))

    this.querySelectorAll(this.els.link.selector).forEach(link => {
      link.addEventListener('click', event => {
        event.preventDefault()
      })
    })
  }

  setupPhotoSwipeListeners () {
    this.photoSwipeInstance.on('uiRegister', () => {
      this.addCloseButton()
      this.addPageCounter()
      this.addTrack()
    })
  }

  addCloseButton () {
    this.photoSwipeInstance.pswp.ui.registerElement({
      name: 'custom-close',
      className: 'pswp-Action_Close',
      ariaLabel: 'Close',
      appendTo: 'wrapper',
      isButton: true,
      html: this.closeSvg,
      onClick: () => {
        this.photoSwipeInstance.pswp.close()
      },
    })
  }

  addPageCounter () {
    this.photoSwipeInstance.pswp.ui.registerElement({
      name: 'custom-counter',
      className: 'pswp-Action_Counter',
      appendTo: 'wrapper',
      onInit: (element, pswp) => {
        const update = () => {
          element.textContent = `${pswp.currIndex + 1}/${pswp.getNumItems()}`
        }

        update()
        pswp.on('change', update)
      },
    })
  }

  addTrack () {
    this.photoSwipeInstance.pswp.ui.registerElement({
      name: 'track',
      className: 'pswp-Media_Track',
      appendTo: 'wrapper',
      onInit: (element, pswp) => {
        element.append(
          this.createArrow('previous', () => pswp.prev()),
          this.createThumbs(pswp),
          this.createDots(pswp),
          this.createArrow('next', () => pswp.next()),
        )
      },
    })
  }

  createArrow (direction, onClick) {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = `pswp-Action_Arrow pswp-Action_Arrow-${direction}`
    button.setAttribute(
      'aria-label',
      direction === 'previous' ? 'Previous' : 'Next',
    )
    button.innerHTML = this.arrowSvg
    button.onclick = onClick

    return button
  }

  createThumbs (pswp) {
    const container = document.createElement('div')
    container.className = 'pswp-Media_Thumbs'

    const buttons = this.els.link.elements.map((link, index) => {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'pswp-Media_Thumb'

      const image = document.createElement('img')
      image.className = 'pswp-Media_ThumbImage'
      image.src = this.thumbnailSrc(link)
      image.alt = ''
      image.loading = 'lazy'
      button.appendChild(image)

      button.onclick = () => pswp.goTo(index)
      container.appendChild(button)

      return button
    })

    const update = () => {
      buttons.forEach((button, index) => {
        button.setAttribute('aria-current', String(index === pswp.currIndex))
      })

      const active = buttons[pswp.currIndex]
      if (active) {
        container.scrollTo({
          left:
            active.offsetLeft -
            (container.clientWidth - active.clientWidth) / 2,
          behavior: 'smooth',
        })
      }
    }

    update()
    pswp.on('change', update)

    return container
  }

  createDots (pswp) {
    const dotSize = 7
    const gap = 4

    const container = document.createElement('div')
    container.className = 'pswp-Action_Dots'

    const total = pswp.getNumItems()
    const dynamic = total > STATIC_DOTS_MAX
    container.classList.toggle('pswp-Action_Dots-dynamic', dynamic)

    const dots = []
    for (let i = 0; i < total; i++) {
      const dot = document.createElement('button')
      dot.type = 'button'
      dot.className = 'pswp-Action_Dot'
      dot.onclick = () => pswp.goTo(i)
      container.appendChild(dot)
      dots.push(dot)
    }

    const update = () => {
      const first = dynamic
        ? Math.min(Math.max(pswp.currIndex - 1, 0), total - DOTS_WINDOW_SIZE)
        : 0
      const last = first + DOTS_WINDOW_SIZE - 1

      const left = `${(first - 2) * (dotSize + gap) * -1}px`

      dots.forEach((dot, index) => {
        dot.setAttribute('aria-current', String(index === pswp.currIndex))

        if (!dynamic) return

        const tier =
          index >= first && index <= last
            ? 'main'
            : index === first - 1 || index === last + 1
              ? 'edge'
              : index === first - 2 || index === last + 2
                ? 'far'
                : null

        dot.classList.toggle('pswp-Action_Dot-main', tier === 'main')
        dot.classList.toggle('pswp-Action_Dot-edge', tier === 'edge')
        dot.classList.toggle('pswp-Action_Dot-far', tier === 'far')
        // dot.hidden = tier === null

        dot.style.left = left
      })
    }

    update()
    pswp.on('change', update)

    return container
  }

  thumbnailSrc (link) {
    try {
      const url = new URL(link.href)
      url.searchParams.set('width', '120')
      return url.href
    } catch {
      return link.href
    }
  }

  handleClick (event) {
    if (event.target.matches('.pswp__img')) {
      const photoSwipeEl = event.target.closest('.pswp')
      if (!photoSwipeEl) return
      photoSwipeEl.setAttribute(
        'data-cursor',
        String(
          this.photoSwipeInstance?.pswp?.currSlide?.currZoomLevel === 2
            ? 'minify'
            : 'magnify',
        ),
      )
    }
  }

  get padding () {
    const bottom = window.matchMedia('(min-width: 768px)').matches ? 100 : 0

    return { top: 0, bottom, left: 0, right: 0 }
  }

  get secondaryZoomLevel () {
    switch (viewportSize()) {
      case 'xs':
        return 0.5
      case 'sm':
        return 0.5
      default:
        return 1
    }
  }

  get arrowSvg () {
    return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 15 14"><path fill="currentColor" fill-rule="evenodd" d="m14.992 7.107.328-.345-.328-.345L8.88 0l-.724.69 5.303 5.568H0v1h13.468l-5.311 5.576.724.69 6.112-6.417Z" clip-rule="evenodd"/></svg>`
  }

  get closeSvg () {
    return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="9.31 9.01 18.68 18.97" aria-hidden="true"><line x1="27.4868" y1="9.80936" x2="9.80915" y2="27.487" stroke="currentColor"/><line x1="9.80915" y1="9.51339" x2="27.4868" y2="27.1911" stroke="currentColor"/></svg>`
  }
}

if (!customElements.get('product-photoswipe')) {
  customElements.define('product-photoswipe', ProductPhotoSwipe)
}
